import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { getOrCreateSmartAccount } from '../services/smartAccountService';

const API_URL = process.env.REACT_APP_API_URL || 'https://tscm-backend.vercel.app';

interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
}

interface SmartAccountStatus {
  isCreating: boolean;
  address: string | null;
  error: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  smartAccountStatus: SmartAccountStatus;
  login: (email: string, password: string) => Promise<void>;
  sendOtp: (email: string) => Promise<void>;
  register: (email: string, password: string, otp: string, displayName?: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  sendPasswordResetOtp: (email: string) => Promise<void>;
  resetPassword: (email: string, otp: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  dismissSmartAccountStatus: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [smartAccountStatus, setSmartAccountStatus] = useState<SmartAccountStatus>({
    isCreating: false,
    address: null,
    error: null,
  });
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const getStoredTokens = () => {
    return {
      accessToken: localStorage.getItem('accessToken'),
      refreshToken: localStorage.getItem('refreshToken'),
    };
  };

  const setStoredTokens = (accessToken: string, refreshToken: string) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  };

  const clearStoredTokens = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  };

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const { refreshToken } = getStoredTokens();
    if (!refreshToken) return null;

    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        clearStoredTokens();
        setUser(null);
        return null;
      }

      const data = await response.json();
      if (data.success) {
        setStoredTokens(data.accessToken, data.refreshToken);
        return data.accessToken;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  // Set up periodic token refresh (every 6 days to stay ahead of 7-day expiry)
  const startTokenRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    refreshIntervalRef.current = setInterval(async () => {
      const { accessToken } = getStoredTokens();
      if (accessToken) {
        await refreshAccessToken();
      }
    }, 6 * 24 * 60 * 60 * 1000);
  }, [refreshAccessToken]);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  const fetchUser = useCallback(async () => {
    const { accessToken } = getStoredTokens();
    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.status === 401) {
        // Try to refresh token
        const newToken = await refreshAccessToken();
        if (newToken) {
          const retryResponse = await fetch(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${newToken}` },
          });
          if (retryResponse.ok) {
            const data = await retryResponse.json();
            if (data.success) {
              setUser(data.user);
              localStorage.setItem('user', JSON.stringify(data.user));
              startTokenRefreshInterval();
            }
          }
        } else {
          clearStoredTokens();
          setUser(null);
        }
      } else if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUser(data.user);
          localStorage.setItem('user', JSON.stringify(data.user));
          startTokenRefreshInterval();
        }
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
      // Don't clear tokens on network error - user might be offline
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch {
          // Invalid stored user
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [refreshAccessToken, startTokenRefreshInterval]);

  // Create smart account in background after auth
  const initSmartAccount = useCallback(async () => {
    const { accessToken } = getStoredTokens();
    if (!accessToken) return;

    setSmartAccountStatus({ isCreating: true, address: null, error: null });

    try {
      const account = await getOrCreateSmartAccount();
      setSmartAccountStatus({
        isCreating: false,
        address: account.address || account.eoaAddress,
        error: null,
      });
    } catch (err) {
      console.error('Smart account creation failed:', err);
      setSmartAccountStatus({
        isCreating: false,
        address: null,
        error: err instanceof Error ? err.message : 'Failed to create smart account',
      });
    }
  }, []);

  // Handle OAuth callback
  useEffect(() => {
    const handleOAuthCallback = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const refresh = urlParams.get('refresh');
      const userParam = urlParams.get('user');
      const errorParam = urlParams.get('error');

      if (errorParam) {
        setError(`Authentication failed: ${errorParam}`);
        window.history.replaceState({}, document.title, window.location.pathname);
        setIsLoading(false);
        return;
      }

      if (token && refresh && userParam) {
        try {
          setStoredTokens(token, refresh);
          const userData = JSON.parse(decodeURIComponent(userParam));
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          startTokenRefreshInterval();
          // Clean up URL
          window.history.replaceState({}, document.title, '/');
          // Create smart account in background
          setTimeout(() => initSmartAccount(), 500);
        } catch (err) {
          console.error('Failed to parse OAuth callback:', err);
          setError('Failed to process login');
        }
        setIsLoading(false);
        return true;
      }
      return false;
    };

    // Check if this is an OAuth callback
    if (window.location.pathname === '/auth/callback' || window.location.search.includes('token=')) {
      handleOAuthCallback();
    } else {
      // Try to restore session from storage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch {
          // Invalid stored user
        }
      }
      fetchUser();
    }
  }, [fetchUser, startTokenRefreshInterval, initSmartAccount]);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Login failed');
      }

      setStoredTokens(data.accessToken, data.refreshToken);
      setUser(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
      startTokenRefreshInterval();
      // Create smart account in background
      setTimeout(() => initSmartAccount(), 500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const sendOtp = async (email: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to send verification code');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send verification code';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, otp: string, displayName?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, otp, displayName }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Registration failed');
      }

      setStoredTokens(data.accessToken, data.refreshToken);
      setUser(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
      startTokenRefreshInterval();
      // Create smart account in background
      setTimeout(() => initSmartAccount(), 500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/google/url`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to get Google auth URL');
      }

      // Redirect to Google OAuth
      window.location.href = data.authUrl;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Google login failed';
      setError(message);
      setIsLoading(false);
      throw err;
    }
  };

  const sendPasswordResetOtp = async (email: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to send verification code');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send verification code';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string, otp: string, newPassword: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, newPassword }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Password reset failed');
      }

      setStoredTokens(data.accessToken, data.refreshToken);
      setUser(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
      startTokenRefreshInterval();
      setTimeout(() => initSmartAccount(), 500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Password reset failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    const { refreshToken } = getStoredTokens();

    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      // Logout locally even if server request fails
    }

    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    clearStoredTokens();
    setUser(null);
    setSmartAccountStatus({ isCreating: false, address: null, error: null });
  };

  const clearError = () => setError(null);
  const dismissSmartAccountStatus = () => setSmartAccountStatus({ isCreating: false, address: null, error: null });

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        error,
        smartAccountStatus,
        login,
        sendOtp,
        register,
        loginWithGoogle,
        sendPasswordResetOtp,
        resetPassword,
        logout,
        clearError,
        dismissSmartAccountStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
